import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from './lib/api';
import {
  Container, Typography, Paper, TextField, Button, Grid, Box, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, Card, CardContent, Chip, useMediaQuery, useTheme
} from '@mui/material';
import {
  Add, Edit, Delete, TrendingUp, Payment, AccountBalance,
  CreditCard, CalendarMonth, BarChart, Receipt
} from '@mui/icons-material';
import DateRangePickerField from './components/DateRangePickerField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { el } from 'date-fns/locale';
import { usePermissions } from './hooks/usePermissions';
import { PERMISSIONS } from './utils/permissions';
import { AccessDenied } from './components/PermissionGate';

export default function Revenue() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { can } = usePermissions();
  const canView = can(PERMISSIONS.REVENUE_VIEW);
  const canCreate = can(PERMISSIONS.REVENUE_CREATE);
  const canEdit = can(PERMISSIONS.REVENUE_EDIT);
  const canDelete = can(PERMISSIONS.REVENUE_DELETE);
  const [revenues, setRevenues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRevenue, setEditingRevenue] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    cash_amount: '',
    card_amount: '',
    notes: ''
  });

  const [stats, setStats] = useState({
    total_cash: 0,
    total_card: 0,
    total_revenue: 0,
    avg_daily_revenue: 0,
    total_days: 0
  });

  useEffect(() => {
    fetchRevenues();
    fetchStats();
  }, [startDate, endDate]);

  const fetchRevenues = async () => {
    setLoading(true);
    try {
      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];
      const response = await fetchWithAuth(`/api/revenue?startDate=${start}&endDate=${end}`);
      const data = await response.json();
      setRevenues(data);
    } catch (error) {
      showSnackbar('Σφάλμα φόρτωσης εσόδων', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];
      const response = await fetchWithAuth(`/api/revenue/summary/stats?startDate=${start}&endDate=${end}`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (revenue = null) => {
    if (revenue) {
      setEditingRevenue(revenue);
      setFormData({
        date: revenue.date,
        cash_amount: revenue.cash_amount,
        card_amount: revenue.card_amount,
        notes: revenue.notes || ''
      });
    } else {
      setEditingRevenue(null);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        cash_amount: '',
        card_amount: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRevenue(null);
  };

  const handleSubmit = async () => {
    if (!formData.cash_amount && !formData.card_amount) {
      showSnackbar('Παρακαλώ εισάγετε τουλάχιστον ένα ποσό', 'error');
      return;
    }

    try {
      const method = editingRevenue ? 'PUT' : 'POST';
      const url = editingRevenue ? `/api/revenue/${editingRevenue.id}` : '/api/revenue';
      
      const response = await fetchWithAuth(url, {
        method,
        body: JSON.stringify({
          ...formData,
          cash_amount: parseFloat(formData.cash_amount) || 0,
          card_amount: parseFloat(formData.card_amount) || 0
        })
      });

      if (response.ok) {
        showSnackbar(editingRevenue ? 'Έσοδα ενημερώθηκαν' : 'Έσοδα καταχωρήθηκαν', 'success');
        fetchRevenues();
        fetchStats();
        handleCloseDialog();
      } else {
        showSnackbar('Σφάλμα αποθήκευσης', 'error');
      }
    } catch (error) {
      showSnackbar('Σφάλμα αποθήκευσης', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το έσοδο;')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`/api/revenue/${id}`, { method: 'DELETE' });
      if (response.ok) {
        showSnackbar('Έσοδο διαγράφηκε', 'success');
        fetchRevenues();
        fetchStats();
      } else {
        showSnackbar('Σφάλμα διαγραφής', 'error');
      }
    } catch (error) {
      showSnackbar('Σφάλμα διαγραφής', 'error');
    }
  };

  const formatCurrency = (amount) => {
    return `€${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (!canView) {
    return <AccessDenied message="Δεν έχετε πρόσβαση στη διαχείριση εσόδων." />;
  }

  const statsCards = [
    {
      label: 'Συνολικά Έσοδα',
      value: formatCurrency(stats.total_revenue),
      icon: <TrendingUp sx={{ color: '#fff', fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
      shadow: 'rgba(34, 197, 94, 0.3)',
      bg: 'rgba(34, 197, 94, 0.06)',
    },
    {
      label: 'Μετρητά',
      value: formatCurrency(stats.total_cash),
      icon: <Payment sx={{ color: '#fff', fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      shadow: 'rgba(59, 130, 246, 0.3)',
      bg: 'rgba(59, 130, 246, 0.06)',
    },
    {
      label: 'Κάρτα',
      value: formatCurrency(stats.total_card),
      icon: <CreditCard sx={{ color: '#fff', fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      shadow: 'rgba(139, 92, 246, 0.3)',
      bg: 'rgba(139, 92, 246, 0.06)',
    },
    {
      label: 'Μ.Ο. / Ημέρα',
      value: formatCurrency(stats.avg_daily_revenue),
      subtitle: `${stats.total_days || 0} ημέρες`,
      icon: <BarChart sx={{ color: '#fff', fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      shadow: 'rgba(249, 115, 22, 0.3)',
      bg: 'rgba(249, 115, 22, 0.06)',
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={el}>
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4 }}>
        {/* Page Header */}
        <Box sx={{ mb: { xs: 2.5, md: 3.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 2,
              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(34, 197, 94, 0.3)'
            }}>
              <TrendingUp sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Ημερήσια Έσοδα
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Καταγραφή & παρακολούθηση εσόδων
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
          {statsCards.map((card, i) => (
            <Grid item xs={6} md={3} key={i}>
              <Card sx={{
                borderRadius: 3,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid rgba(0,0,0,0.06)',
                transition: 'all 0.2s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 25px rgba(0,0,0,0.1)' }
              }}>
                <CardContent sx={{ p: { xs: 1.5, md: 2.5 }, '&:last-child': { pb: { xs: 1.5, md: 2.5 } } }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                        {card.label}
                      </Typography>
                      <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 700, mt: 0.5 }}>
                        {card.value}
                      </Typography>
                      {card.subtitle && (
                        <Typography variant="caption" color="text.secondary">{card.subtitle}</Typography>
                      )}
                    </Box>
                    <Box sx={{
                      width: { xs: 36, md: 44 }, height: { xs: 36, md: 44 }, borderRadius: 2,
                      background: card.gradient,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: `0 4px 14px ${card.shadow}`, flexShrink: 0
                    }}>
                      {card.icon}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Filters */}
        <Paper sx={{
          p: { xs: 1.5, md: 2 }, mb: { xs: 2, md: 3 }, borderRadius: 3,
          border: '1px solid rgba(0,0,0,0.06)',
          background: 'linear-gradient(135deg, rgba(34,197,94,0.02) 0%, rgba(22,163,74,0.05) 100%)',
        }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <DateRangePickerField
                startDate={startDate}
                endDate={endDate}
                onRangeChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
                isMobile={isMobile}
                label="Περίοδος"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              {canCreate && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog()}
                  fullWidth
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, py: 1 }}
                >
                  Νέο Έσοδο
                </Button>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Revenue Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Ημερομηνία</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }} align="right">Μετρητά</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }} align="right">Κάρτα</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }} align="right">Σύνολο</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>Σημειώσεις</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }} align="center">Ενέργειες</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {revenues.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 8, textAlign: 'center' }}>
                    <Receipt sx={{ fontSize: 56, color: 'grey.300', mb: 2, display: 'block', mx: 'auto' }} />
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Δεν βρέθηκαν έσοδα για την επιλεγμένη περίοδο
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      Προσθέστε ένα νέο έσοδο ή αλλάξτε τα φίλτρα ημερομηνίας
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                revenues.map((revenue) => {
                  const total = (revenue.cash_amount || 0) + (revenue.card_amount || 0);
                  return (
                    <TableRow key={revenue.id} sx={{ transition: 'background 0.15s', '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarMonth sx={{ fontSize: 16, color: 'text.disabled' }} />
                          {formatDate(revenue.date)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={formatCurrency(revenue.cash_amount)}
                          size="small"
                          sx={{ fontWeight: 600, borderRadius: '8px', bgcolor: 'rgba(59,130,246,0.08)', color: '#2563eb' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          label={formatCurrency(revenue.card_amount)}
                          size="small"
                          sx={{ fontWeight: 600, borderRadius: '8px', bgcolor: 'rgba(139,92,246,0.08)', color: '#7c3aed' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#16a34a' }}>
                          {formatCurrency(total)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {revenue.notes || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          {canEdit && (
                            <IconButton size="small" onClick={() => handleOpenDialog(revenue)} title="Επεξεργασία"
                              sx={{ bgcolor: 'rgba(59,130,246,0.08)', borderRadius: 2, '&:hover': { bgcolor: 'rgba(59,130,246,0.15)' } }}>
                              <Edit sx={{ fontSize: 18, color: '#3b82f6' }} />
                            </IconButton>
                          )}
                          {canDelete && (
                            <IconButton size="small" onClick={() => handleDelete(revenue.id)} title="Διαγραφή"
                              sx={{ bgcolor: 'rgba(239,68,68,0.08)', borderRadius: 2, '&:hover': { bgcolor: 'rgba(239,68,68,0.15)' } }}>
                              <Delete sx={{ fontSize: 18, color: '#ef4444' }} />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
          <DialogTitle sx={{
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            color: '#fff', py: 2.5,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {editingRevenue ? <Edit sx={{ color: '#fff' }} /> : <Add sx={{ color: '#fff' }} />}
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
                  {editingRevenue ? 'Επεξεργασία Εσόδου' : 'Νέο Έσοδο'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  Ημερήσια καταγραφή εσόδων
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: '24px !important' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Ημερομηνία"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Μετρητά (€)"
                  type="number"
                  value={formData.cash_amount}
                  onChange={(e) => setFormData({ ...formData, cash_amount: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  InputProps={{
                    startAdornment: <Payment sx={{ mr: 1, color: '#3b82f6', fontSize: 20 }} />,
                  }}
                />
                <TextField
                  label="Κάρτα (€)"
                  type="number"
                  value={formData.card_amount}
                  onChange={(e) => setFormData({ ...formData, card_amount: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  InputProps={{
                    startAdornment: <CreditCard sx={{ mr: 1, color: '#8b5cf6', fontSize: 20 }} />,
                  }}
                />
              </Box>
              <TextField
                label="Σημειώσεις"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                fullWidth
                multiline
                rows={3}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Box sx={{
                p: 2, borderRadius: 2,
                background: 'linear-gradient(135deg, rgba(34,197,94,0.06) 0%, rgba(22,163,74,0.1) 100%)',
                border: '1px solid rgba(34,197,94,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Σύνολο Ημέρας
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#16a34a' }}>
                  {formatCurrency((parseFloat(formData.cash_amount) || 0) + (parseFloat(formData.card_amount) || 0))}
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button onClick={handleCloseDialog} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
              Ακύρωση
            </Button>
            <Button onClick={handleSubmit} variant="contained" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
              Αποθήκευση
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
}
